import { Paintbrush, Grid3x3, Layers, ImagePlus, MessageSquareText, Sofa, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import BeforeAfterCompare from "@/components/landing/BeforeAfterCompare";
import { WALL_COLORS, FLOOR_STYLES, WALLPAPERS } from "@/lib/room-refresh-options";
import cafeB from "@/assets/room-refresh-showcase/cafe-before.jpg";
import cafeA from "@/assets/room-refresh-showcase/cafe-after.jpg";
import hotelB from "@/assets/room-refresh-showcase/hotel-lobby-before.jpg";
import hotelA from "@/assets/room-refresh-showcase/hotel-lobby-after.jpg";
import officeB from "@/assets/room-refresh-showcase/office-before.jpg";
import officeA from "@/assets/room-refresh-showcase/office-after.jpg";
import recB from "@/assets/room-refresh-showcase/reception-before.jpg";
import recA from "@/assets/room-refresh-showcase/reception-after.jpg";
import retailB from "@/assets/room-refresh-showcase/retail-before.jpg";
import retailA from "@/assets/room-refresh-showcase/retail-after.jpg";
import homeB from "@/assets/room-refresh-showcase/residential-before.jpg";
import homeA from "@/assets/room-refresh-showcase/residential-after.jpg";
import restaurantB from "@/assets/room-refresh-showcase/restaurant-before.jpg";
import restaurantA from "@/assets/room-refresh-showcase/restaurant-after.jpg";
import salonB from "@/assets/room-refresh-showcase/salon-before.jpg";
import salonA from "@/assets/room-refresh-showcase/salon-after.jpg";
import studioB from "@/assets/room-refresh-showcase/studio-before.jpg";
import studioA from "@/assets/room-refresh-showcase/studio-after.jpg";

const CAPABILITIES = [
  { icon: Paintbrush, title: "New wall color", text: "Pick from 10 curated shades — from Sage Green to Terracotta." },
  { icon: Grid3x3, title: "New floor or tiles", text: "Try wood, marble, terrazzo, checkerboard and more (10 options)." },
  { icon: Layers, title: "Wallpaper on every wall", text: "Choose a pattern — it even takes on the wall color you picked." },
  { icon: ImagePlus, title: "Add things you love", text: "Upload up to 2 photos of a sofa, lamp or wallpaper to use in your room." },
  { icon: MessageSquareText, title: "Say it in your words", text: "Type any change, like “add more plants” or “make it cozier”." },
  { icon: Sofa, title: "Tidy & rearrange", text: "Your furniture gets a better layout — windows, doors and walls stay put." },
];

const EXAMPLES = [
  { label: "Cafe", treatment: "Wallpaper + tile", change: "Olive botanical wallpaper + terracotta patterned tiles", before: cafeB, after: cafeA },
  { label: "Hotel lobby", treatment: "Wallpaper + marble", change: "Dusty-blue damask wallpaper + white marble tiles", before: hotelB, after: hotelA },
  { label: "Retail boutique", treatment: "Wallpaper + tile", change: "Blush geometric wallpaper + checkerboard tiles", before: retailB, after: retailA },
  { label: "Restaurant", treatment: "Wallpaper + tile", change: "Burgundy mural wallpaper + encaustic floor tiles", before: restaurantB, after: restaurantA },
  { label: "Beauty salon", treatment: "Wallpaper + terrazzo", change: "Soft-green botanical wallpaper + rose terrazzo floor", before: salonB, after: salonA },
  { label: "Office / co-working", treatment: "Paint + wood", change: "Muted sage paint + pale oak flooring", before: officeB, after: officeA },
  { label: "Reception / waiting", treatment: "Paint + terrazzo", change: "Deep navy paint + white terrazzo tiles", before: recB, after: recA },
  { label: "Creative studio", treatment: "Paint + floor", change: "Bold cobalt paint + light microcement floor", before: studioB, after: studioA },
  { label: "Home living room", treatment: "Paint + wood", change: "Warm clay paint + wide-plank walnut flooring", before: homeB, after: homeA },
];

const LEFT_CAPABILITIES = CAPABILITIES.slice(0, 3);
const RIGHT_CAPABILITIES = CAPABILITIES.slice(3);
const LEFT_POSITIONS = [
  "lg:col-start-1 lg:row-start-1",
  "lg:col-start-1 lg:row-start-2",
  "lg:col-start-1 lg:row-start-3",
];
const RIGHT_POSITIONS = [
  "lg:col-start-3 lg:row-start-1",
  "lg:col-start-3 lg:row-start-2",
  "lg:col-start-3 lg:row-start-3",
];

function CapabilityCard({ capability }: { capability: (typeof CAPABILITIES)[number] }) {
  const Icon = capability.icon;

  return (
    <div className="h-full rounded-lg border border-border bg-card p-5 sm:p-6">
      <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </div>
      <h3 className="text-[16px] font-semibold text-foreground">{capability.title}</h3>
      <p className="mt-1.5 text-[14px] leading-[1.6] text-muted-foreground">{capability.text}</p>
    </div>
  );
}

export default function RoomRefreshShowcase({ onTry }: { onTry: () => void }) {
  return (
    <section className="py-16 sm:py-24 lg:py-32 bg-background" aria-labelledby="rr-title">
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
        <div className="mb-14 grid gap-4 sm:mb-20 sm:grid-cols-2 lg:grid-cols-[1fr_1.5fr_1fr] lg:grid-rows-3">
          <div className="order-first flex min-h-[460px] flex-col justify-between overflow-hidden rounded-lg border border-border bg-card sm:col-span-2 lg:col-span-1 lg:col-start-2 lg:row-span-3 lg:row-start-1 lg:min-h-0">
            <div className="p-6 sm:p-8 lg:p-10">
              <div className="ed-caps mb-3 text-primary">ROOM REFRESH</div>
              <h2 id="rr-title" className="text-[26px] font-bold leading-[1.15] text-foreground sm:text-[32px] lg:text-[40px]">
                Keep your space. Give it a fresh new look.
              </h2>
              <p className="mt-4 text-[15px] leading-[1.7] text-muted-foreground sm:text-[16px]">
                Keep your furniture, windows, doors and room shape exactly where they belong. Refresh the layout,
                walls, floors and finishing touches instead.
              </p>
              <Button size="lg" onClick={onTry} className="mt-7 rounded-full px-7">
                Try Room Refresh <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </Button>
            </div>

            <div className="border-t border-border bg-muted/40 p-6 sm:p-8">
              <div className="mb-4 flex items-center justify-between gap-4">
                <span className="ed-caps text-muted-foreground">Choose your new look</span>
                <span className="text-[12px] text-muted-foreground">All optional</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className={`aspect-square rounded-md border border-border ${WALL_COLORS[7].swatchClass}`} />
                  <p className="mt-2 text-[12px] font-medium text-foreground">Wall color</p>
                  <p className="text-[11px] text-muted-foreground">10 shades</p>
                </div>
                <div>
                  <img src={FLOOR_STYLES[3].image} alt="Light wood floor option" className="aspect-square w-full rounded-md border border-border object-cover" loading="lazy" />
                  <p className="mt-2 text-[12px] font-medium text-foreground">Floor / tile</p>
                  <p className="text-[11px] text-muted-foreground">10 styles</p>
                </div>
                <div>
                  <img src={WALLPAPERS[2].image} alt="Botanical wallpaper option" className="aspect-square w-full rounded-md border border-border object-cover" loading="lazy" />
                  <p className="mt-2 text-[12px] font-medium text-foreground">Wallpaper</p>
                  <p className="text-[11px] text-muted-foreground">10 patterns</p>
                </div>
              </div>
            </div>
          </div>

          {LEFT_CAPABILITIES.map((capability, index) => (
            <div key={capability.title} className={LEFT_POSITIONS[index]}>
              <CapabilityCard capability={capability} />
            </div>
          ))}
          {RIGHT_CAPABILITIES.map((capability, index) => (
            <div key={capability.title} className={RIGHT_POSITIONS[index]}>
              <CapabilityCard capability={capability} />
            </div>
          ))}
        </div>

        <div className="mb-6 max-w-2xl sm:mb-8">
          <div className="ed-caps mb-2 text-primary">Finish-led transformations</div>
          <h3 className="text-[20px] font-bold text-foreground sm:text-[24px]">For homes and businesses</h3>
          <p className="mt-2 text-[14px] leading-[1.65] text-muted-foreground sm:text-[15px]">
            Compare completely different combinations of paint, wallpaper, flooring and tiles. Drag each divider to
            see the finishes change while the room stays familiar.
          </p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {EXAMPLES.map((e) => (
            <article key={e.label} className="overflow-hidden rounded-lg border border-border bg-card">
              <BeforeAfterCompare
                before={e.before}
                after={e.after}
                beforeAlt={`${e.label} before Room Refresh`}
                afterAlt={`${e.label} after Room Refresh: ${e.change}`}
                className="aspect-[4/3] w-full"
                rounded="rounded-none"
                initial={50}
              />
              <div className="px-4 py-3 border-t border-border">
                <div className="flex items-center justify-between gap-3">
                  <div className="ed-caps text-muted-foreground">{e.label}</div>
                  <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-semibold uppercase text-primary">
                    {e.treatment}
                  </span>
                </div>
                <div className="text-[14px] font-medium text-foreground mt-1">{e.change}</div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
